// Dashboard-Karte „Lüftung & Heizung" (Tim 25.10.2026: „Auf dem Dashboard
// wird noch eine Karte benötigt, die die Temp. Lüftung außen, innen und die
// Heizung zeigt. Vlt. mit einer schicken Grafik" -- Wahl „A · Kurven",
// Entwurf docs/mockups/dashboard-lueftung-heizung.html). Rein gerechnet,
// ohne Stores; die Seite reicht die Verläufe herein (lib/dashboard.test.mjs).

import type { LageTeil } from "../lage";

/** Die drei Fühler, gefunden über ihren Namen (Aqara, Zigbee). Die beiden
 *  Lüftungsfühler waren vertauscht benannt und sind am 25.10.2026 in
 *  zigbee2mqtt getauscht worden: „außen" fällt nachts ab. */
export const LUEFTUNG_FUEHLER = [
  { schluessel: "aussen", name: "Lüftung außen" },
  { schluessel: "innen", name: "Lüftung innen" },
  { schluessel: "heizung", name: "Heizung" },
] as const;

export type FuehlerSchluessel = (typeof LUEFTUNG_FUEHLER)[number]["schluessel"];

/** Ein Messpunkt: Unix-Sekunden und Wert. */
export interface Messpunkt {
  t: number;
  v: number;
}

/** Ab dieser Temperatur am Heizungsfühler läuft die Heizung: in Ruhe misst
 *  er die Raumluft (um 21–22 °C), geheizt 28–33 °C (Verlauf 23.–25.10.). */
export const HEIZT_AB = 25;

/** Bringt mehrere Reihen auf EINE Zeitachse, wie sie das Diagramm braucht
 *  (uPlot: xs und je Reihe ein Wert je x). Ein Fühler meldet nur bei
 *  Änderung -- dazwischen gilt sein letzter Wert (Stufe). Vor seinem
 *  ersten Punkt im Fenster: null. Die Achse reicht bis `bis`, damit jede
 *  Kurve bis jetzt läuft. */
export function aufEineAchse(reihen: Messpunkt[][], bis: number): [number[], ...(number | null)[][]] {
  const ts = new Set<number>();
  for (const r of reihen) for (const p of r) if (p.t <= bis) ts.add(p.t);
  if (ts.size) ts.add(bis);
  const xs = [...ts].sort((a, b) => a - b);
  const ys = reihen.map((r) => {
    const sortiert = [...r].sort((a, b) => a.t - b.t);
    const out: (number | null)[] = [];
    let i = 0;
    let letzter: number | null = null;
    for (const x of xs) {
      while (i < sortiert.length && sortiert[i].t <= x) letzter = sortiert[i++].v;
      out.push(letzter);
    }
    return out;
  });
  return [xs, ...ys];
}

/** Abschnitte, in denen die Heizung lief (Fühler ≥ HEIZT_AB), als
 *  [von, bis] in Unix-Sekunden -- das Diagramm legt sie grau hinterlegt. */
export function heizPhasen(heizung: Messpunkt[], bis: number, ab = HEIZT_AB): [number, number][] {
  const out: [number, number][] = [];
  let an: number | null = null;
  for (const p of [...heizung].sort((a, b) => a.t - b.t)) {
    if (p.t > bis) break;
    if (p.v >= ab && an === null) an = p.t;
    if (p.v < ab && an !== null) {
      out.push([an, p.t]);
      an = null;
    }
  }
  if (an !== null) out.push([an, bis]);
  return out;
}

const de = (v: number): string => v.toFixed(1).replace(".", ",");
const uhr = (t: number): string => {
  const d = new Date(t * 1000);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

/** Lage-Satz: läuft die Heizung (seit wann), und wie weit die Außenluft
 *  über den Tag schwankte. Ohne Daten: nichts. */
export function lueftungLage(aussen: Messpunkt[], heizung: Messpunkt[], jetztHeizung: number | null, bis: number): LageTeil[] {
  const teile: LageTeil[] = [];
  if (jetztHeizung !== null) {
    const phasen = heizPhasen(heizung, bis);
    const letzte = phasen[phasen.length - 1];
    if (jetztHeizung >= HEIZT_AB && letzte && letzte[1] === bis) teile.push({ text: `Heizung läuft seit ${uhr(letzte[0])}` });
    else teile.push({ text: jetztHeizung >= HEIZT_AB ? "Heizung läuft" : "Heizung aus" });
  }
  const werte = aussen.filter((p) => p.t <= bis).map((p) => p.v);
  if (werte.length) teile.push({ text: `außen ${de(Math.min(...werte))}–${de(Math.max(...werte))} °C`, titel: "Spanne der Außenluft im gezeigten Zeitraum" });
  return teile;
}
